/**
 * `useDesigns` — live read of every file for a project, sorted
 * newest-update first.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/designs/useDesigns.ts
 *
 * Returns:
 *   - `data` — flat array sorted by `updatedAt` desc
 *   - `loading`
 *   - `canEdit` — role gate; mirrors mobile's `design.write` capability
 *     (org owner or role in {superAdmin, admin, manager,
 *     siteEngineer, designer})
 *
 * Uses the existing `(orgId, projectId)` composite index already
 * present in firestore.indexes.json.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { canEditTasks, type RoleKey } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import type { Design } from '@/lib/designs';
import { useFullOrganization } from '@/lib/useFullOrganization';

export type UseDesignsResult = {
  data: Design[];
  loading: boolean;
  canEdit: boolean;
};

export function useDesigns(
  projectId: string | undefined,
  orgId: string | undefined,
): UseDesignsResult {
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const [data, setData] = useState<Design[]>([]);
  const [loading, setLoading] = useState(true);

  const canEdit = useMemo(() => {
    if (!user || !org) return false;
    const orgData = org.data as {
      ownerId?: string;
      roles?: Record<string, string>;
    };
    if (orgData.ownerId === user.uid) return true;
    const role = orgData.roles?.[user.uid] as RoleKey | undefined;
    return canEditTasks(role);
  }, [user, org]);

  useEffect(() => {
    if (!projectId || !orgId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'designs'),
      where('orgId', '==', orgId),
      where('projectId', '==', projectId),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: Design[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Design, 'id'>),
        }));
        // Newest-updated first; rows missing updatedAt sink.
        rows.sort((a, b) => {
          const at = a.updatedAt?.toMillis() ?? 0;
          const bt = b.updatedAt?.toMillis() ?? 0;
          return bt - at;
        });
        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useDesigns] snapshot error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [projectId, orgId]);

  return { data, loading, canEdit };
}
